import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { GridComponent } from '@app/core/components/grid/grid.component';
import { ColDef } from '@ag-grid-community/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { UtilityService } from '@app/core/services/utility.service';
import { EdiParams, EdiService, EdiSetupGet, EdiSetupRow } from '@app/services/edi.service';

@Component({
    selector: 'tt-edi-setup',
    templateUrl: './edi-setup.component.html',
    styleUrls: ['./edi-setup.component.css'],
    standalone: false,
})
export class EdiSetupComponent implements OnInit {
    public page?: EdiSetupGet;
    public grid?: Grid<EdiSetupRow>;
    @ViewChild(GridComponent) public gridView?: GridComponent<EdiSetupRow>;
    public newCustomer = false;
    public editing = false;
    private readonly canceledAdd = new Error('EDI setup creation canceled');
    private params: EdiParams = { argtype: '', argvalue: '' };

    constructor(private readonly edi: EdiService, private readonly utility: UtilityService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        this.params = { argtype: String(this.navigator?.params['argtype'] ?? ''), argvalue: String(this.navigator?.params['argvalue'] ?? '') };
        this.newCustomer = this.utility.toBoolean(String(this.navigator?.params['new_customer'] ?? ''), false);
        this.page = await this.edi.getSetup(this.params);
        this.grid = new Grid<EdiSetupRow>().setRememberId('ediSetup.ttGrid')
            .setLoadDataFunction({ method: 2265, parameters: this.params })
            .setAddRowFunction({ method: null, parameters: null, autoSave: true, pre: row => this.prepareNewRow(row) })
            .setSaveDataFunction({ method: null, parameters: {}, readAfterSave: true })
            .setToolbar({ add: false, save: false, edit: false, lock: false })
            .setSpecialFunc({ newTab: false, buttons: [{ name: 'edit', text: '', icon: 'far fa-chevron-right', func: row => this.editRow(row) }] })
            .setKendoConfig({ height: '100%', aggregate: false, pager: false, selectable: 'multiple', filterable: true });
        this.grid.onSetup = (_schema, _fields, columns) => this.setupColumns(columns);
    }

    public setupColumns(columns: ColDef<EdiSetupRow>[]): void {
        const fields = ['ediTypeKeyno', 'p2ReportdefKeyno', 'p2ExportprocKeyno', 'ediDestinationKeyno'];
        for (const column of columns) {
            if (!column.field || !fields.includes(column.field)) continue;
            const field = column.field;
            column.valueFormatter = params => this.getName(field, params.data);
            column.cellRenderer = (params: { data?: EdiSetupRow }) => {
                const span = document.createElement('span'); span.textContent = this.getName(field, params.data); return span;
            };
        }
    }

    public getName(field: string, row?: EdiSetupRow): string {
        if (!row) return '';
        const type = this.page?.ediTypes?.['ediType' + row.ediTypeKeyno];
        if (field === 'ediTypeKeyno') return type?.name ?? '';
        const items = field === 'p2ReportdefKeyno' ? type?.reportdefs : field === 'p2ExportprocKeyno' ? type?.exportprocs : this.page?.ediDestinations;
        return Object.values(items ?? {}).find(item => item.keyno === row[field])?.name ?? '';
    }

    public async addRow(): Promise<void> {
        if (!this.grid?.gridfunc || !this.page || this.editing) return;
        this.editing = true;
        try {
            await this.grid.gridfunc.addRowBefore(0);
            await this.grid.gridfunc.read();
        } catch (error) {
            if (error !== this.canceledAdd) throw error;
        } finally { this.editing = false; }
    }

    private async prepareNewRow(row?: EdiSetupRow): Promise<void> {
        if (!row || !this.page || !this.grid?.gridfunc) throw this.canceledAdd;
        const response = await this.edi.openSetupEditor({ ...this.params, dataItem: row, data: this.page, rows: this.grid.gridfunc.getAllRows() });
        if (!response || response.delete) throw this.canceledAdd;
    }

    public async editRow(row?: EdiSetupRow): Promise<void> {
        if (!row || !this.grid?.gridfunc || !this.page || this.editing) return;
        this.editing = true;
        try {
            const response = await this.edi.openSetupEditor({ ...this.params, dataItem: row, data: this.page, rows: this.grid.gridfunc.getAllRows() });
            if (!response) return;
            if (response.delete) {
                await this.grid.gridfunc.removeRow(row);
                await this.grid.gridfunc.saveChanges();
            } else if (row.dirty) {
                // Translate the legacy editor's dirty fields into the current grid save contract.
                if (row._uuid && this.gridView?.options.data) this.gridView.options.data.dirtyRows[row._uuid] = {
                    cells: Object.keys(row.dirtyFields ?? {}).filter(field => row.dirtyFields?.[field]),
                };
                row._dirty = true;
                await this.grid.gridfunc.saveSingleRow(row, 'update');
            }
            await this.grid.gridfunc.read();
        } finally { this.editing = false; }
    }

    public goToCustomers(): void { this.navigator?.go({ to: 'findcustomers' }); }
    public goToNewCustomer(): void { this.navigator?.go({ to: 'customeredit', parms: { cust_no: this.params.argtype === 'cust_no' ? this.params.argvalue : 0 } }); }
}
